import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import reactHtmlParser from 'react-html-parser';
import { PopOutIcon } from '../shared/PopOutIcon';
import Svg from '../shared/Svg';
import PropTypes from 'prop-types';
import { deleteTimeRecord } from '../../actions/time';
import moment from 'moment';
import colors from '../../utils/colors';
import * as RootActions from '../../actions/index';
import * as TimeActions from '../../actions/time';
import { deleteDynamoTimeRecord } from '../../../api/emailsApi/time';
import { Pencil } from '../shared/Pencil';
import TimeBillingErrorsDisplay from '../timeFormContainer/TimeBillingErrorsDisplay';
import { secondsToTimeDurationString } from '../../utils/time';
import { handleAddTime } from '../../utils/time';

import './TimeRecordItem.scss';

export const TimeRecordItem = ({
  popoutLink,
  relatedTo,
  entryDate,
  assignedTo,
  taskCode,
  loggedTime,
  isSaved,
  isPresaved,
  index,
  isError,
  timeRecord,
  activeErrorMessage,
  setActiveErrorMessage,
}) => {
  const dispatch = useDispatch();
  const [isTooltipVisible, setTooltipVisible] = useState(false);
  const emailId = useSelector((state) => state.mail.activeEmail);
  const isTimeProcessing = useSelector((state) => state.time.timeProcessing);
  const durationFormat = useSelector((state) => state.time.config.settings?.durationFormat);
  const cachedSelectedTimekeepers = useSelector((state) => state.time?.timekeepersSelected);
  const salesforceUserId = useSelector((state) => state.auth.user?.salesforceUserId);
  const salesforceDisplayName = useSelector((state) => state.auth.user?.salesforceDisplayName);
  const ownerEmail = useSelector((state) => state.mail.ownerEmail);

  const displaySettings = useSelector((state) => state.mail.settings?.objectsAndDisplayFields);
  const displayField = displaySettings.find((setting) => setting.apiName === relatedTo?.type)?.displayFields[0]?.apiName;
  const objectsConfiguredForTime = useSelector((state) => state.time?.config?.objectsConfiguredForTime) || [];
  const allSelectedRecords = useSelector((state) => state.mail.selectedRecords);
  const selectedRecords = Object.fromEntries(Object.entries(allSelectedRecords).filter(([key, obj]) => objectsConfiguredForTime.includes(obj.type))) || {};
  const timeConfigFields = useSelector((state) => state.time.config?.fields);

  const removeTimeRecord = async () => {
    dispatch(deleteTimeRecord(index));
    // if a error message is found we need to delete the time record from dynamo
    if (timeRecord.errorMessage) {
      try {
        await deleteDynamoTimeRecord(timeRecord?.PK?.split('#')?.[1] || timeRecord.emailRecordId, timeRecord?.SK?.split('#')?.[1] || timeRecord.timeRecordId, emailId, ownerEmail);
      } catch (e) {
        dispatch(RootActions.changeAlertText('Error deleting time record from DynamoDB'));
      }
    }
  };

  const editTimeRecord = () => {
    handleAddTime(false, dispatch, false, false, false, selectedRecords, timeConfigFields);
    dispatch(TimeActions.setSelectedTimeRecord({ ...timeRecord, recordIndex: index }));
  };

  const toggleTooltip = () => {
    setActiveErrorMessage(index);
    setTooltipVisible(!isTooltipVisible);
  };

  useEffect(() => {
    if (activeErrorMessage !== index) {
      setTooltipVisible(false);
    }
  }, [activeErrorMessage]);


  return (
    <div className={`timeRecordItem-container ${isError ? 'timeRecordItem-error' : ''}`}>
      <Svg className="timeRecordItem-image" data='/assets/time.svg'/>
      <div className='timeRecordItem-details'>
        <p className='timeRecordItem-name'>{reactHtmlParser(relatedTo?.[displayField])}</p>
        <p className='timeRecordItem-subDetail'>Assigned to: {assignedTo || cachedSelectedTimekeepers[timeRecord?.OwnerId] || ((timeRecord?.OwnerId === salesforceUserId) ? salesforceDisplayName : timeRecord?.OwnerId)}</p>
        <p className='timeRecordItem-subDetail'>{moment(entryDate).format('MMM D, YYYY')} {taskCode && <span>&#8226;</span>} {taskCode || ''}</p>
        <p className='timeRecordItem-subDetail'>Time Entry: {secondsToTimeDurationString(loggedTime, durationFormat)}</p>
      </div>
      <div className='timeRecordItem-iconsContainer'>
        {timeRecord.errorMessage &&
          <>
            <TimeBillingErrorsDisplay
              type='block-error'
              errorArray={[timeRecord.errorMessage]}
              isTooltipVisible={isTooltipVisible}
              setTooltipVisible={setTooltipVisible}
              toggleTooltip={toggleTooltip}
              hideCount={true}
              title='Time Entry Error'
              size='small'
              parent='TimeRecordItem'
              setActiveErrorMessage={setActiveErrorMessage}
            />
          </>
        }
        { isPresaved ? <div className='timeRecordItem-presaveSpacer' /> : (isSaved && !timeRecord.errorMessage) ?
          <PopOutIcon link={popoutLink} className={'timeRecordItem-removeMargin'} isDocrio={false}/> :
          <>
            <Pencil
              className='timeRecordItem-edit'
              onClickCallback={ !isTimeProcessing ? editTimeRecord : () => {} }
              tooltipContent='Edit entry'
              hoverable
              fill = {isTimeProcessing ? colors.lightGrey : colors.searchGrey}
              hoverFill={isTimeProcessing ? colors.lightGrey : colors.iconHover}
              disabled = {isTimeProcessing}
            />
            <Svg
              className={ isTimeProcessing ? 'timeRecordItem-delete-disabled' : 'timeRecordItem-delete' }
              data='/assets/trash.svg'
              fill={isTimeProcessing ? colors.lightGrey : colors.searchGrey}
              onClickAction={ !isTimeProcessing ? removeTimeRecord : () => {} }
              hoverable
              hoverFill={isTimeProcessing ? colors.lightGrey : colors.iconHover}
              tooltipContent={isTimeProcessing ? '' : 'Remove'}
            />
          </>
        }
      </div>
    </div>
  );
};

TimeRecordItem.propTypes = {
  popoutLink: PropTypes.string,
  relatedTo: PropTypes.object,
  entryDate: PropTypes.string,
  assignedTo: PropTypes.string,
  taskCode: PropTypes.string,
  loggedTime: PropTypes.number,
  isSaved: PropTypes.bool,
  isPresaved: PropTypes.bool,
  index: PropTypes.number,
  isError: PropTypes.bool,
  timeRecord: PropTypes.object,
  activeErrorMessage: PropTypes.number,
  setActiveErrorMessage: PropTypes.func,
};
